export const formatNumber = (num: number, numberLocale = 'fa-IR'): string => {
  return new Intl.NumberFormat(numberLocale).format(num)
}

export const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}

export const getCoinIcon = (coinType: string): string => {
  switch (coinType) {
    case 'gold':
      return '🥇'
    case 'diamond':
      return '💎'
    case 'silver':
      return '🥈'
    case 'bronze':
      return '🥉'
    default:
      return '🪙'
  }
}

/** Fallback Persian labels — prefer `t('coins.*')` when locale context is available. */
export const getCoinName = (coinType: string): string => {
  switch (coinType) {
    case 'gold':
      return 'طلایی'
    case 'diamond':
      return 'الماسی'
    case 'silver':
      return 'نقره'
    case 'bronze':
      return 'برنزی'
    default:
      return 'سکه'
  }
}

export const coinNameKey = (coinType: string): string => {
  switch (coinType) {
    case 'gold':
    case 'diamond':
    case 'silver':
    case 'bronze':
      return `coins.${coinType}`
    default:
      return 'coins.coin'
  }
}

export const calculateCountdown = (target: Date): string => {
  const now = new Date().getTime()
  const distance = target.getTime() - now

  if (distance < 0) return '00:00:00'

  const hours = Math.floor(distance / (1000 * 60 * 60))
  const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((distance % (1000 * 60)) / 1000)

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

export const copyToClipboard = (text: string): boolean => {
  try {
    navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

/** Random multi-digit public id from API (not derived from internal id). */
export const displayUserPublicId = (user: { public_id?: string } | null | undefined): string => {
  return user?.public_id?.trim() || ''
}

/** Berlin local parts for airdrop window (closed 23:00–00:00 Europe/Berlin). */
export const getBerlinParts = (date: Date = new Date()): { hours: number; minutes: number; seconds: number } => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0)
  return { hours: num('hour'), minutes: num('minute'), seconds: num('second') }
}

export const getTimeUntilGameEnd = (): { hours: number; minutes: number; seconds: number; isClosed: boolean } => {
  const { hours: berlinHours, minutes: berlinMinutes, seconds: berlinSeconds } = getBerlinParts()
  const GAME_END_HOUR = 23

  let totalSeconds: number
  const isClosed = berlinHours >= GAME_END_HOUR
  if (!isClosed) {
    totalSeconds = (GAME_END_HOUR - berlinHours) * 3600 - berlinMinutes * 60 - berlinSeconds
  } else {
    totalSeconds = (24 - berlinHours) * 3600 - berlinMinutes * 60 - berlinSeconds
  }

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.floor(totalSeconds % 60)

  return { hours, minutes, seconds, isClosed }
}

export const formatUsefulCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

export const formatDate = (dateString: string, numberLocale = 'fa-IR'): string => {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat(numberLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  } catch {
    return dateString
  }
}
